/**
 * Sofia Connect — North-south (WAN) bandwidth model for AI data centres.
 *
 * Pure, server-safe, no React, no DOM. The /tools/bandwidth-calculator/
 * client imports from here. Extracted the same way pricing.ts was — one
 * source of truth for the numbers the UI, the tests and any future API
 * route all read.
 *
 * The math is the product. What matters:
 *
 *   - Sizing is per megawatt of installed IT load, not per GPU. GPU
 *     count per watt changes every ~18 months; north-south traffic per
 *     watt barely moves. Anything indexed on GPU count ages badly.
 *
 *   - Average and peak are dominated by DIFFERENT flows. Average is
 *     inference I/O + checkpoint duty cycle; peak is almost entirely
 *     checkpoint drain. Never conflate them.
 *
 *   - Five independent flows: checkpoint, dataset ingest, inference,
 *     distillation/copy-out, ops.
 *
 * Coefficients are modelling assumptions, not Sofia Connect
 * measurements. The site has been through a long exercise removing
 * unsupported figures — do NOT edit these numbers to make a customer's
 * quote come out nicer. If the assumptions are wrong, argue about the
 * assumptions.
 *
 * All intermediates are in Gb/s. Convert to Tb/s only for display.
 */

/* ═════════════════════════════════════════════════════
   Inputs and archetype coefficients
   ═════════════════════════════════════════════════════ */

export type Archetype = "cap" | "mix" | "inf";
export const ARCHETYPES: readonly Archetype[] = ["cap", "mix", "inf"] as const;

/** Human-readable archetype labels used in i18n and analytics. */
export const ARCHETYPE_KEYS = {
  cap: "captive",
  mix: "mixed",
  inf: "inference",
} as const;

export interface Coefficients {
  /** Multiplier applied to inference base I/O. */
  infIntensity: number;
  /** Share of site load doing training (drives dataset ingest). */
  trainShare: number;
  /**
   * Checkpoint retention factor, bytes retained per parameter across all
   * shards drained per event.
   *   2  = weights only (bf16)
   *   6  = bf16 + fp32 master
   *   14 = full optimizer state (Adam m + v)
   *   18 = plus EMA
   */
  R: number;
}

/**
 * Archetype coefficient table. Never mutate at runtime.
 * If a customer disputes a value, change it here and in the tests
 * together, not in the UI.
 */
export const COEFFS: Readonly<Record<Archetype, Coefficients>> = {
  cap: { infIntensity: 0.2, trainShare: 0.9, R: 14 },
  mix: { infIntensity: 1.0, trainShare: 0.6, R: 14 },
  inf: { infIntensity: 4.0, trainShare: 0.15, R: 2 },
};

/** Input bounds and defaults — the sliders read from here. */
export const INPUT_BOUNDS = {
  mw: { min: 5, max: 500, step: 5, default: 100 },
  P: { min: 0.1, max: 5, step: 0.1, default: 1.0 },
  drain: { min: 30, max: 1800, step: 30, default: 120 },
  interval: { min: 5, max: 240, step: 5, default: 30 },
  bursters: { min: 1, max: 8, step: 1, default: 3 },
} as const;

export interface BandwidthInputs {
  arch: Archetype;
  /** Installed IT load (MW). */
  mw: number;
  /** Largest model on site (trillion parameters). */
  P: number;
  /** Checkpoint drain window per burster (seconds). */
  drain: number;
  /** Checkpoint interval per burster (minutes between events). */
  interval: number;
  /** Independent burster count (concurrent training runs). */
  bursters: number;
}

/** Default input state — kept in one place so URL restore and reset agree. */
export const DEFAULT_INPUTS: BandwidthInputs = {
  arch: "mix",
  mw: INPUT_BOUNDS.mw.default,
  P: INPUT_BOUNDS.P.default,
  drain: INPUT_BOUNDS.drain.default,
  interval: INPUT_BOUNDS.interval.default,
  bursters: INPUT_BOUNDS.bursters.default,
};

/* ═════════════════════════════════════════════════════
   Flow identity — used by the UI breakdown and by tests
   ═════════════════════════════════════════════════════ */

export type FlowId = "checkpoint" | "dataset" | "inference" | "distill" | "ops";
export const FLOW_IDS: readonly FlowId[] = [
  "checkpoint",
  "dataset",
  "inference",
  "distill",
  "ops",
] as const;

/* ═════════════════════════════════════════════════════
   Model core
   ═════════════════════════════════════════════════════ */

/**
 * Correlation factor Φ across independent bursters.
 *
 * With one burster it is by definition 1 (a single tenant cannot
 * de-correlate with itself). With two or more, each additional burster
 * shaves 0.05 off, floored at 0.4 — contractually staggered drain
 * windows tend toward that floor and this is the biggest single lever
 * a wholesale customer has on required capacity.
 */
export function phiFor(bursters: number): number {
  if (bursters <= 1) return 1;
  return Math.max(0.4, 0.75 - (bursters - 2) * 0.05);
}

export interface FlowBreakdown {
  avg: number; // Gb/s
  peak: number; // Gb/s
}

export interface BandwidthResult {
  inputs: BandwidthInputs;
  coeffs: Coefficients;
  /** Correlation factor Φ applied to checkpoint peak. */
  phi: number;
  /** Per-flow avg/peak in Gb/s. */
  flows: Record<FlowId, FlowBreakdown>;
  /** Site totals in Tb/s. */
  totals: {
    avgTbps: number;
    peakTbps: number;
    /** Required link at 65% target utilisation, Tb/s. */
    linkTbps: number;
  };
  perMw: {
    avgGbps: number;
    peakGbps: number;
  };
  /**
   * Rounded UP to even lambda counts across two diverse routes.
   * Only 400G: it is the largest step in BW_DWDM (pricing.ts). An 800G
   * alternative was removed because Sofia Connect does not sell 800G.
   */
  lambdas: {
    l400: number;
  };
}

/**
 * Compute the model for a given input set.
 *
 * Intermediates are in Gb/s. Rounding happens at the display boundary —
 * see `formatTbps` / `formatGbps`.
 */
export function computeBandwidth(inputs: BandwidthInputs): BandwidthResult {
  const { arch, mw, P, drain, interval, bursters } = inputs;
  const c = COEFFS[arch];

  const phi = phiFor(bursters);

  // Checkpoint (Gb/s) — bytes/param × 8 bits/byte × params over drain window.
  // ck_single is per-burster peak drain rate; ck_peak sums bursters × Φ.
  const ck_single = (P * c.R * 8) / drain * 1000;
  const ck_peak = ck_single * bursters * phi;
  const ck_avg = ck_single * bursters * Math.min(1, drain / (interval * 60));

  // Dataset ingest (Gb/s) — 0.4 avg / 6.0 peak per MW, scaled by trainShare.
  const ds_avg = mw * 0.4 * c.trainShare;
  const ds_peak = mw * 6.0 * c.trainShare;

  // Inference I/O (Gb/s) — 1.35 avg per MW at intensity 1.0, peak = 3.5×.
  const if_avg = mw * 1.35 * c.infIntensity;
  const if_peak = if_avg * 3.5;

  // Distillation / model copy-out (Gb/s).
  const dist_avg = mw * 0.3;
  const dist_peak = mw * 3.0;

  // Ops: telemetry, image pull, control plane (Gb/s).
  const ops_avg = mw * 0.15;
  const ops_peak = mw * 0.4;

  const avgGbps = ck_avg + ds_avg + if_avg + dist_avg + ops_avg;
  const peakGbps = ck_peak + ds_peak + if_peak + dist_peak + ops_peak;

  const avgTbps = avgGbps / 1000;
  const peakTbps = peakGbps / 1000;

  // Required physical link at 65% target utilisation.
  const linkTbps = peakTbps / 0.65;

  // Even lambda count across two diverse routes.
  const l400 = Math.ceil(linkTbps / 0.4) * 2;

  return {
    inputs,
    coeffs: c,
    phi,
    flows: {
      checkpoint: { avg: ck_avg, peak: ck_peak },
      dataset: { avg: ds_avg, peak: ds_peak },
      inference: { avg: if_avg, peak: if_peak },
      distill: { avg: dist_avg, peak: dist_peak },
      ops: { avg: ops_avg, peak: ops_peak },
    },
    totals: { avgTbps, peakTbps, linkTbps },
    perMw: {
      avgGbps: avgGbps / mw,
      peakGbps: peakGbps / mw,
    },
    lambdas: { l400 },
  };
}

/* ═════════════════════════════════════════════════════
   Display helpers — all rounding lives here.
   No raw float should reach the DOM.
   ═════════════════════════════════════════════════════ */

/** Round to N decimals, avoiding -0. */
export function round(n: number, decimals: number): number {
  const f = 10 ** decimals;
  const r = Math.round(n * f) / f;
  return Object.is(r, -0) ? 0 : r;
}

/** Format a value in Tb/s at the requested decimal count. */
export function formatTbps(tbps: number, decimals: number): string {
  return round(tbps, decimals).toFixed(decimals);
}

/**
 * Format a bandwidth in Gb/s.
 *   < 1000: "NNN G"     (integer)
 *   ≥ 1000: "N.N T"     (one decimal, Tb/s)
 */
export function formatGbps(gbps: number): string {
  if (gbps < 1000) return `${Math.round(gbps)} G`;
  return `${round(gbps / 1000, 1).toFixed(1)} T`;
}

/**
 * Serialise inputs into a URL query string (stable key order).
 * Restored by parseSearchParams below.
 */
export function inputsToSearchParams(
  inputs: BandwidthInputs,
): Record<string, string> {
  return {
    arch: inputs.arch,
    mw: String(inputs.mw),
    P: String(inputs.P),
    drain: String(inputs.drain),
    interval: String(inputs.interval),
    bursters: String(inputs.bursters),
  };
}

/**
 * Restore inputs from a query-string bag, clamped to the declared
 * bounds so nobody can drive the calculator into a non-physical state
 * by editing the URL.
 */
export function parseSearchParams(
  params: URLSearchParams | Record<string, string | string[] | undefined>,
): BandwidthInputs {
  const get = (k: string): string | undefined => {
    if (params instanceof URLSearchParams) return params.get(k) ?? undefined;
    const v = params[k];
    if (Array.isArray(v)) return v[0];
    return v;
  };

  const clamp = (n: number, min: number, max: number) =>
    Math.min(max, Math.max(min, n));
  const num = (raw: string | undefined, fallback: number) => {
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  };

  const archRaw = get("arch");
  const arch: Archetype = (ARCHETYPES as readonly string[]).includes(
    archRaw ?? "",
  )
    ? (archRaw as Archetype)
    : DEFAULT_INPUTS.arch;

  return {
    arch,
    mw: clamp(num(get("mw"), DEFAULT_INPUTS.mw), INPUT_BOUNDS.mw.min, INPUT_BOUNDS.mw.max),
    P: clamp(num(get("P"), DEFAULT_INPUTS.P), INPUT_BOUNDS.P.min, INPUT_BOUNDS.P.max),
    drain: clamp(
      num(get("drain"), DEFAULT_INPUTS.drain),
      INPUT_BOUNDS.drain.min,
      INPUT_BOUNDS.drain.max,
    ),
    interval: clamp(
      num(get("interval"), DEFAULT_INPUTS.interval),
      INPUT_BOUNDS.interval.min,
      INPUT_BOUNDS.interval.max,
    ),
    bursters: clamp(
      Math.round(num(get("bursters"), DEFAULT_INPUTS.bursters)),
      INPUT_BOUNDS.bursters.min,
      INPUT_BOUNDS.bursters.max,
    ),
  };
}
